import React, { useCallback, useRef } from "react";
import {
    LinkingOptions,
    NavigationContainer,
    NavigatorScreenParams,
    useNavigationContainerRef,
} from "@react-navigation/native";
import * as ExpoNotifications from "expo-notifications";
import { AuthManagerContext, AuthStatus } from "../contexts/AuthManager";
import { useTheme } from "../hooks/use-theme";
import * as Sentry from "@sentry/react-native";
import queryString from "query-string";
import {
    GetSalaryDetailsQuery,
    EarningType,
    WorkerInfo,
    CctDetailsFieldFullValue,
    Cct,
    User,
    MissionFee,
    MissionDeduction,
} from "../__generated__/graphql";
import * as Linking from "expo-linking";
import { convertUrlToCustomScheme, deepValuesMap, getNotificationData } from "../utils";
import Navigator from "./Navigator";
import { ClientTabParamList } from "./client/Home";
import { WorkerTabParamList } from "./worker/Home";
import { usePushNotification } from "../hooks/push-notification";
import { useTracking } from "../hooks/tracking";
import { Platform } from "react-native";
import { ListItemProps } from "../ui_atoms/ListItem";
import AppLoadingManager from "../contexts/AppLoadingManager";
import ErrorBoundary from "../ui_atoms/ErrorBoundary";
import {
    ProfileSwitcherManagerContext,
    ProfileIdType,
    findProfileForUser,
} from "../contexts/ProfileSwitcherManager";
import ProfileSwitcher from "../components/ProfileSwitcher";
import WebImageChooserPortal from "../components/webimages/WebimageChooserPortal";
import UserAvatarPortal from "../components/avatars/UserAvatarPortal";
import { useSnackbar } from "../hooks/snackbar";
import { useI18n } from "../hooks/i18n-hooks";
import { SignupSigninScreens } from "./SignupSigninStack";
import { ActionType } from "../screens/user/ContractSigningScreen";

export type RootStackParamList = {
    Welcome?: { isLogin: string };
    PresentationCarousel: undefined;
    SignupSigninStack?: { isSignup?: boolean; initialRouteName?: SignupSigninScreens };
    CheckWorkerInfo: undefined;
    CheckClientInfo?: { isNewProfile: boolean };
    CheckPrivateClientInfo?: { isNewProfile: boolean };
    VeriffScreen: undefined;
    PreIdVerification: undefined;
    IdVerificationPending: undefined;
    IdVerificationRetry: undefined;
    ClientVerificationPending: undefined;
    VerificationFailed: undefined;
    PostOnboardingPrepare: undefined;
    SignupFinished: undefined;
    Smile: undefined;
    Home: NavigatorScreenParams<ClientTabParamList> | NavigatorScreenParams<WorkerTabParamList>;
    PostOnboardingProfileEditor?: { newProfileId: ProfileIdType };
    CivilStateEdit?: { profileId: ProfileIdType };
    FiscalAttestationEdit?: { profileId: ProfileIdType };
    PrivateEmailEdit?: { profileId: ProfileIdType };
    HomeAddressEdit?: { profileId: ProfileIdType };
    ChildrenEdit?: { profileId: ProfileIdType };
    BankAccountEdit: undefined;
    BankAccountEditModal: { profileId?: ProfileIdType }; // for post onboarding
    DiplomaAndTrainingEdit: undefined;
    ClientProfileEdit: undefined;
    WorkerProfileEdit: undefined;
    PhoneNumberEdit: undefined;
    ClientEdit: undefined;
    AccountEdit: undefined;
    PrivateClientEdit: undefined;
    ConfigureLpp: undefined;
    ContractSigningScreen: { contractId: string; initialState?: ActionType };
    CreateMissionStack: { workerInfo?: WorkerInfo } | undefined;
    MissionDetails: { missionId: string };
    CctDetails: { cct: Cct; cctDetails: CctDetailsFieldFullValue };
    MissionTermination: { missionId: string } | undefined;
    MissionTimeSheets: { missionId: string };
    MissionClientFees: { fees: ({ key: string } & ListItemProps)[] };
    MissionInfoDeductions: { deductions: ({ key: string } & ListItemProps)[] };
    MissionLogs: { missionId: string };
    SalaryCountingDetails: {
        earningType: EarningType;
        salaryDetails: Pick<
            GetSalaryDetailsQuery["getSalaryDetails"],
            | "workerDeductions"
            | "invoiceSalary"
            | "netSalary"
            | "lseMargin"
            | "grossSalary"
            | "vacationCompensation"
            | "publicHolidaysCompensation"
            | "thirteenMonthCompensation"
            | "baseSalary"
            | "clientDeductions"
        >;
    };
    PayrollBreakdownList: { missionId: string };
    PayrollBreakdownDetails: { payrollBreakdownId: string };
    PayrollBreakdownEditor: { payrollBreakdownId: string };
    PayrollBreakdownTimeSheetScreen: { payrollBreakdownId: string };
    TimesheetList: undefined;
    TimesheetClientReview: { id: string };
    TimesheetWorkerReview: { id: string };
    TimesheetWorkerInput: { id: string };
    ResendEmailVerification: undefined;
    Confirmation: undefined;
    PdfViewer: { assetId: string; title?: string; filename?: string };
    ClientInvitationDetails: { invitationId: string };
    WorkerInvitationDetails: { invitationId: string };
    SendInvitation: undefined;
    InviteClientInfo: undefined;
    IntercomScreen: undefined;
    ClientList: undefined;
    ClientDetails: { clientProfileId: string };
    InvoiceDetails: { invoiceId: string };
    SalaryDetails: { salaryId: string };
    FeesDetails: { fees: MissionFee[]; deductions: MissionDeduction[] };
    NotFoundScreen?: { error?: Error; goBack?: boolean; isNotFound?: boolean };
    SkribbleSampleScreen: undefined;
    IJMEditorNavScreen: undefined;
    ContractPreviewScreen: { contractId: string };
    MissionSelectSignerScreen: { missionId: string };
    IJMClientViewer: undefined;
};

const prefix = Linking.createURL("");

const linkingConfig = {
    initialRouteName: "Home",
    screens: {
        Welcome: "app/welcome",
        TimesheetWorkerInput: "app/timesheets/:id",
        TimesheetWorkerReview: "app/timesheets/:id/worker-review",
        TimesheetClientReview: "app/timesheets/:id/client-review",
        WorkerInvitationDetails: "app/invitations/:invitationId/worker",
        ClientInvitationDetails: "app/invitations/:invitationId/client",
        SalaryDetails: "app/salaries/:salaryId",
        InvoiceDetails: "app/invoices/:invoiceId",
        Invoices: "app/invoices",
        Colleagues: "app/colleagues",
        Home: {
            initialRouteName: "MissionsScreen",
            screens: {
                SalariesScreen: "app/salaries",
                MissionsScreen: {
                    initialRouteName: "MissionList",
                    screens: {
                        MissionDetails: "app/missions/:missionId",
                        MissionList: "app/missions",
                    },
                },
                WorkersScreen: "app/employees",
                ActivitiesTab: {
                    initialRouteName: "TodoList",
                    screens: {
                        TodoList: "app/todos",
                    },
                },
                WorkerTimesheetList: "app/timesheets",
                ProfileStack: {
                    initialRouteName: "Profile",
                    screens: {
                        Profile: "app/profile",
                        Colleagues: "app/profile/colleagues",
                    },
                },
            },
        },
        ContractSigningScreen: "app/contract_signing/:contractId",
        ContractPreviewScreen: "app/contract_preview/:contractId",
        MissionSelectSignerScreen: "app/mission_select_signer/:missionId",
        PayrollBreakdownDetails: "app/payroll_breakdowns/details/:payrollBreakdownId",
        PayrollBreakdownEditor: "app/payroll_breakdowns/review/:payrollBreakdownId",
    },
};

const routesList = deepValuesMap(linkingConfig, "initialRouteName");

const matchRoute = (route: string) =>
    !!routesList.find((v) => {
        const regexPattern = v.replace(/:[^\\/]+/g, "[^\\/]+");
        const regex = new RegExp(`^${regexPattern}$`);
        return regex.test(route);
    });

export default function RootNavigation() {
    const theme = useTheme();
    const { status, user, refreshUser } = React.useContext(AuthManagerContext);
    const { profileId: currentProfileId, setProfile } = React.useContext(
        ProfileSwitcherManagerContext,
    );
    const navigationRef = useNavigationContainerRef<RootStackParamList>();
    const routeNameRef = useRef<string>();
    const { trackScreenView } = useTracking();
    const { showSnackbar } = useSnackbar();
    const i18n = useI18n();

    usePushNotification();

    const switchDeeplinkProfile = useCallback(
        async (rootUrl: string) => {
            if (typeof rootUrl !== "string") {
                console.warn("switchDeeplinkProfile - rootUrl is not a string", typeof rootUrl);
                return "";
            }

            const { url, query } = queryString.parseUrl(rootUrl);

            if (Object.keys(query).length > 0) {
                const { profile_id } = query as { profile_id: string };

                let currentUser = user;

                if (!currentUser) {
                    // In the case of getInitialURL(), `user` is undefined so we have to refetch it
                    currentUser = (await refreshUser?.()) as User;
                }

                if (currentUser && profile_id !== currentProfileId) {
                    const profile = findProfileForUser(profile_id, currentUser);
                    if (profile) {
                        await setProfile(profile);
                    } else {
                        showSnackbar({
                            text: i18n.t("profile_switch.not_found"),
                            mode: "error",
                        });
                    }
                }
            }

            return url;
        },
        [user, currentProfileId, refreshUser, setProfile, showSnackbar, i18n],
    );

    const linking: LinkingOptions<RootStackParamList> = {
        prefixes: [prefix],
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        config: linkingConfig,
        async getInitialURL() {
            // Check if app was opened from a deep link
            let initialUrl = await Linking.getInitialURL();

            if (initialUrl != null) {
                initialUrl = await switchDeeplinkProfile(initialUrl);

                let finalUrl = initialUrl.replace(/\/$/, "");

                if (initialUrl.startsWith("https://") || initialUrl.startsWith("http://")) {
                    // if the deep link is a web url, we want to extract the path
                    finalUrl = convertUrlToCustomScheme(initialUrl);
                }

                if (finalUrl.endsWith("/app")) {
                    if (status === AuthStatus.In) {
                        finalUrl = `${prefix}app/missions`;
                    } else {
                        finalUrl = `${prefix}app/welcome`;
                    }
                }
                if (matchRoute(finalUrl.replace(prefix, ""))) {
                    return finalUrl;
                } else {
                    Sentry.captureException(
                        new Error(`The following link does not exists in the app: ${finalUrl}`),
                    );
                }
            }

            // Handle URL from expo push notifications
            const response = await ExpoNotifications.getLastNotificationResponseAsync();

            const appUrl = response
                ? (getNotificationData(response.notification.request.content).app_url as string)
                : undefined;

            return appUrl ? await switchDeeplinkProfile(appUrl) : "/";
        },
        // Custom function to subscribe to incoming links
        subscribe(listener) {
            const unsubscribe = ExpoNotifications.addNotificationResponseReceivedListener(
                // eslint-disable-next-line @typescript-eslint/no-misused-promises
                async (notificationResponse) => {
                    try {
                        listener(
                            await switchDeeplinkProfile(
                                getNotificationData(
                                    notificationResponse.notification.request.content,
                                ).app_url as string,
                            ),
                        );
                    } catch (error) {
                        // catch the exception to avoid unicorn crash on the app when notification
                        // are badly formatted
                        Sentry.captureException(error, {
                            data: {
                                info: "Error while handling notification response",
                                contentName:
                                    notificationResponse.notification.request.content?.title,
                                contentData:
                                    notificationResponse.notification.request.content?.data,
                            },
                        });
                    }
                },
            );
            const linkingSubscription = Linking.addEventListener("url", ({ url }) => {
                void switchDeeplinkProfile(url.replace(/\/$/, "")).then((finalUrl) => {
                    if (finalUrl.startsWith("https://") || finalUrl.startsWith("http://")) {
                        // if the deep link is a web url, we want to extract the path
                        finalUrl = convertUrlToCustomScheme(finalUrl);
                    }

                    if (finalUrl.endsWith("/app")) {
                        if (status === AuthStatus.In) {
                            finalUrl = `${prefix}app/missions`;
                        } else {
                            finalUrl = `${prefix}app/welcome`;
                        }
                    }
                    if (matchRoute(finalUrl.replace(prefix, ""))) {
                        listener(finalUrl);
                    } else {
                        Sentry.captureException(
                            new Error(`The following link does not exists in the app: ${finalUrl}`),
                        );
                    }
                });
            });

            return () => {
                // Clean up the event listeners
                ExpoNotifications.removeNotificationSubscription(unsubscribe);
                linkingSubscription.remove();
            };
        },
    };

    const onNavigationReady = useCallback(() => {
        const currentRoute = navigationRef?.getCurrentRoute();
        routeNameRef.current = currentRoute?.name;
        if (Platform.OS === "web") {
            // If there is a trailing profile_id param in the url, ProfileSwitcherManager.tsx:284 will use it then we remove it here
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            navigationRef?.setParams({ profile_id: undefined });
        }
    }, [navigationRef]);

    const onNavigationStateChange = useCallback(() => {
        const previousRouteName = routeNameRef.current;
        const currentRouteName = navigationRef?.getCurrentRoute()?.name;
        if (currentRouteName && previousRouteName !== currentRouteName) {
            // Save the current route name for later comparison
            routeNameRef.current = currentRouteName;
            void trackScreenView(currentRouteName);
        }
    }, [navigationRef, trackScreenView]);

    return (
        <NavigationContainer<RootStackParamList>
            theme={theme}
            linking={linking}
            ref={navigationRef}
            onReady={onNavigationReady}
            onStateChange={onNavigationStateChange}
            navigationInChildEnabled
        >
            {/* the ErrorBoundary and AppLoadingManager must be in the NavigationContainer */}
            <ErrorBoundary>
                <AppLoadingManager>
                    <Navigator />
                    <ProfileSwitcher />
                    <WebImageChooserPortal />
                    <UserAvatarPortal />
                </AppLoadingManager>
            </ErrorBoundary>
        </NavigationContainer>
    );
}
